/**
 * useMapClusters Hook
 *
 * Manages marker clustering for the guard location map using supercluster.
 * Groups nearby guards into clusters at low zoom levels, with alert status tracking.
 *
 * @see Issue 7.1: Implement marker clustering
 */

import { useMemo } from 'react';
import Supercluster from 'supercluster';
import {
  GuardLocation,
  GuardPointProperties,
  GuardPoint,
  ClusterOrPoint,
  MapBounds,
} from './types';

/** Options for clustering behavior */
export interface UseMapClustersOptions {
  /** Cluster radius in pixels (default: 60) */
  radius?: number;
  /** Stop clustering at this zoom level (default: 15) */
  maxZoom?: number;
  /** Minimum points to form a cluster (default: 3) */
  minPoints?: number;
}

/** Return value from useMapClusters hook */
export interface UseMapClustersResult {
  /** Array of clusters and individual points to render */
  clusters: ClusterOrPoint[];
  /** The supercluster instance for expansion zoom calculation */
  supercluster: Supercluster<GuardPointProperties, GuardPointProperties> | null;
  /** Get all guard points within a cluster */
  getClusterLeaves: (clusterId: number) => GuardPoint[];
}

/**
 * Hook for clustering guard markers on the map.
 *
 * @param guards - Array of guard locations to cluster
 * @param bounds - Current map viewport bounds (null = show all as individual)
 * @param zoom - Current map zoom level
 * @param options - Clustering options
 * @returns Clusters/points to render and helper functions
 */
export function useMapClusters(
  guards: GuardLocation[],
  bounds: MapBounds | null,
  zoom: number,
  options?: UseMapClustersOptions
): UseMapClustersResult {
  const { radius = 60, maxZoom = 15, minPoints = 3 } = options ?? {};

  // Convert guards to GeoJSON points with alert status
  const points: GuardPoint[] = useMemo(
    () =>
      guards.map((guard) => ({
        type: 'Feature' as const,
        properties: {
          guardId: guard.guardId,
          name: guard.name,
          status: guard.status,
          batteryPercent: guard.batteryPercent,
          // Alert = stale/offline status OR low battery (<20%)
          hasAlert:
            guard.status === 'stale' ||
            guard.status === 'offline' ||
            (guard.batteryPercent !== null && guard.batteryPercent < 20),
        },
        geometry: {
          type: 'Point' as const,
          coordinates: [guard.longitude, guard.latitude] as [number, number],
        },
      })),
    [guards]
  );

  // Initialize supercluster with custom reduce for alert aggregation
  const supercluster = useMemo(() => {
    const sc = new Supercluster<GuardPointProperties, GuardPointProperties>({
      radius,
      maxZoom,
      minPoints,
      // Map function: transform point properties for clustering
      map: (props) => ({
        ...props,
        // Convert boolean to number for aggregation
        hasAlert: props.hasAlert ? 1 : 0,
      } as unknown as GuardPointProperties),
      // Reduce function: aggregate alert counts across cluster members
      reduce: (accumulated, props) => {
        // Sum up hasAlert counts (treated as numbers during reduce)
        (accumulated as any).hasAlert += (props as any).hasAlert;
      },
    });
    sc.load(points);
    return sc;
  }, [points, radius, maxZoom, minPoints]);

  // Get clusters for current viewport
  const clusters = useMemo(() => {
    // No bounds = return all as individual points (initial load)
    if (!bounds) {
      return points;
    }

    // Convert bounds to bbox format: [westLng, southLat, eastLng, northLat]
    const bbox: [number, number, number, number] = [
      bounds.west,
      bounds.south,
      bounds.east,
      bounds.north,
    ];

    return supercluster.getClusters(bbox, Math.floor(zoom));
  }, [supercluster, bounds, zoom, points]);

  // Helper to get all guard points within a cluster
  const getClusterLeaves = (clusterId: number): GuardPoint[] => {
    return supercluster.getLeaves(clusterId, Infinity);
  };

  return { clusters, supercluster, getClusterLeaves };
}

export default useMapClusters;
